import { z } from 'zod';

export const DeleteAccountRequestSchema = z.object({
  confirmText: z.string().email(),
})

export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequestSchema>

export const ReclaimedTimeMonthSchema = z.object({
  monthStart: z.string(),
  updatedCount: z.number(),
  reclaimedSeconds: z.number(),
});

export const ConversionValueSnapshotSchema = z.object({
  reclaimedSeconds: z.number().int().nonnegative(),
  emailsHandled: z.number().int().nonnegative(),
  mailboxScopeMessagesTotal: z.number().int().nonnegative().nullable(),
  mailboxScopePrecision: z.enum(['derived', 'floored']).nullable(),
  mailboxTotalsFetchedAt: z.string().nullable(),
  available: z.boolean(),
});

export type ConversionValueSnapshot = z.infer<typeof ConversionValueSnapshotSchema>;

export const AccountScanSummaryResponseSchema = z.object({
  thisMonthEmailsScanned: z.number(),
  suggestionsReady: z.number(),
  suggestionsApplied: z.number(),
  sendersCovered: z.number(),
  successfulScans: z.number(),
  reclaimedTimeLastSixMonths: z.array(ReclaimedTimeMonthSchema).length(6),
  reclaimableMinutes: z.number(),
  reclaimedMinutes: z.number(),
  /**
   * Aggregate size in bytes of pending suggestions (issue #390). Sums
   * `emails.size_bytes` for the LEFT JOIN over `classification_suggestions`
   * where status='pending' (excludes 'low_confidence' bookkeeping rows).
   * Suggestions whose underlying email row has been deleted contribute 0.
   * Optional for backward compatibility with older clients.
   */
  reclaimableSizeBytes: z.number().int().nonnegative().optional(),
  /**
   * Aggregate size in bytes already reclaimed via applied suggestions
   * (issue #390). Same join semantics as `reclaimableSizeBytes` but
   * scoped to status='applied'. Best-effort: emails purged from the
   * `emails` table after Trash apply contribute 0.
   */
  reclaimedSizeBytes: z.number().int().nonnegative().optional(),
  planTier: z.enum(['free', 'pro']),
  valueSnapshot: ConversionValueSnapshotSchema,
});

export type AccountScanSummaryResponse = z.infer<typeof AccountScanSummaryResponseSchema>;

/**
 * GDPR DSAR (Data Subject Access Request) export response schema.
 * Returns a JSON bundle of all user-scoped rows so the user can exercise
 * their right of access/portability under GDPR Articles 15 and 20.
 *
 * Sensitive fields (OAuth access/refresh tokens) are stripped from the export;
 * only metadata about OAuth connections is included.
 */
export const DataExportResponseSchema = z.object({
  generated_at: z.string(),
  user_id: z.string(),
  data: z.object({
    auth_user: z.record(z.string(), z.any()).nullable(),
    emails: z.array(z.record(z.string(), z.any())),
    suggestions: z.array(z.record(z.string(), z.any())),
    activity_logs: z.array(z.record(z.string(), z.any())),
    scan_jobs: z.array(z.record(z.string(), z.any())),
    user_stripe_mapping: z.array(z.record(z.string(), z.any())),
    oauth_tokens: z.array(z.record(z.string(), z.any())),
    consent_logs: z.array(z.record(z.string(), z.any())),
    sender_insights: z.array(z.record(z.string(), z.any())),
    // Issue #763: code-catalog companion state. The user's override delta and
    // its audit trail are personal data and must appear in a DSAR export.
    classification_policy_overrides: z.array(z.record(z.string(), z.any())),
    classification_policy_override_history: z.array(z.record(z.string(), z.any())),
    user_email_usage: z.array(z.record(z.string(), z.any())),
    user_terms_acceptance: z.array(z.record(z.string(), z.any())),
    user_preferences: z.array(z.record(z.string(), z.any())),
    apply_jobs: z.array(z.record(z.string(), z.any())),
    product_feedback: z.array(z.record(z.string(), z.any())),
  }),
})

export type DataExportResponse = z.infer<typeof DataExportResponseSchema>
